import 'intersection-observer'
import * as React from 'react'
import { IOWidget, Updater, fromJSX } from 'widgets-for-react'
import { Link } from '../../../../cms/blocks/types.gen'
import { link } from '../../../../cms/blocks/utils.gen'
import { parseImageUrl } from '../../../../drupal/rendering/image_utils.mod'
import { ParsedMediaImage } from '../../../../drupal/types_and_parser/types'
import { CMSState } from '../../../drupalPage/state'

export type ImageWithExpandablesBlockProps = {
  blockIndex: number
  title: string
  introduction: string
  items: Immutable.List<ImageWithExpandablesBlockItem>
  isStyleguide?: boolean
}

export type ImageWithExpandablesBlockItem = {
  title: string
  text: string
  link?: Link
  field_image?: ParsedMediaImage
  image_alt?: string
}

export type ImageWithExpandablesBlockStyle = {}

export function ImageWithExpandablesBlock(
  props: ImageWithExpandablesBlockProps,
  options?: { style: ImageWithExpandablesBlockStyle; key?: string }
): IOWidget<CMSState, Updater<CMSState>> {
  return (s: CMSState) =>
    fromJSX((_) => {
      return <ImageWithExpandablesBlockLayout {...props} />
    })
}

export const ImageWithExpandablesBlockLayout = (props: ImageWithExpandablesBlockProps) => {
  const { title, introduction, items, isStyleguide } = props
  const [activeItemIndex, setActiveItemIndex] = React.useState(0)
  const isCompact = items.count() > 3
  return (
    <section className={`block block--image-with-expandables ${isCompact ? 'is-compact' : 'is-default'}`}>
      <div className="block__inner container container--m grid--block">
        {(title || introduction) && (
          <header className="block__header container container--m">
            {title && <h2 className="block__title title--l">{title}</h2>}
            {introduction && <div className="block__intro" dangerouslySetInnerHTML={{ __html: introduction }} />}
          </header>
        )}

        <div className="block__layout">
          <div className="block__content">
            {!items.isEmpty() &&
              items
                .map((item, index) => {
                  return (
                    <details
                      key={index}
                      className="block__item item"
                      open={index == activeItemIndex}
                      onToggle={(e) => {
                        if (e.currentTarget.open) {
                          setActiveItemIndex(index)
                        }
                      }}
                    >
                      <summary className={`item__summary ${isCompact ? 'title--xs' : 'title--s'}`}>
                        <span className="item__title">{item.title}</span>
                      </summary>
                      <div className="item__body">
                        <p dangerouslySetInnerHTML={{ __html: item.text }} />
                        {item.link && (
                          <>
                            {link(item.link, { classname: 'item__link link link--secondary' }, item.link.title, () => {
                              window.dataLayer.push({
                                event: 'event.refresco',
                                category: 'ImageWithExpandables',
                                action: item.link.title,
                                label: window.location.pathname
                              })
                            })}
                          </>
                        )}
                      </div>
                    </details>
                  )
                })
                .toArray()}
          </div>

          <div className="block__visual fit-object">
            {!items.isEmpty() && items.get(activeItemIndex) && (
              <img
                key={activeItemIndex}
                className="block__image lazyload"
                alt={isStyleguide ? styleguideImages[activeItemIndex].alt : items.get(activeItemIndex).image_alt || ''}
                width="960"
                height="680"
                src="/images/empty.png"
                data-src={
                  isStyleguide
                    ? styleguideImages[activeItemIndex].src
                    : `${parseImageUrl(
                        items.get(activeItemIndex).field_image?.field_media_image?.image?.style_urls['640x360']
                      )}`
                }
                data-srcset={
                  isStyleguide
                    ? undefined
                    : `${parseImageUrl(
                        items.get(activeItemIndex).field_image?.field_media_image?.image?.style_urls['640x360']
                      )} 640w, ${parseImageUrl(
                        items.get(activeItemIndex).field_image?.field_media_image?.image?.style_urls['1280x720']
                      )} 1280w`
                }
                data-sizes="auto"
              />
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

const styleguideImages = [
  {
    src: '/images/backgrounds/bg-drink-cocktail.png',
    alt: 'cocktail'
  },
  {
    src: '/images/backgrounds/bg-drink-cola.png',
    alt: 'cola'
  },
  {
    src: '/images/backgrounds/bg-drink-juice.png',
    alt: 'juice'
  }
]
